import { useMemo } from 'react';
import {
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
} from '@fluentui/react-components';
import { FolderOpen, MoreHorizontal } from 'lucide-react';

import { MigrationGroupWithRollup } from '../api/migrationGroups';
import { DataTable, DataTableColumn } from './DataTable';
import RelativeTime from './RelativeTime';
import { groupLastActivity } from '../lib/groupRollup';
import { NUMERIC, PLAIN_BUTTON, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-472 (R-FD-048) — the Groups workspace as a flat table, replacing FD-280's
// expand-in-place accordion at the owner's request, to match the AirBridge
// design board.
//
// WHAT THE ACCORDION DID THAT THIS DOES NOT, recorded rather than left to be
// rediscovered: FD-280 built it so an engagement was scannable without drilling
// into each group and coming back. A flat table gives that up. The mitigation is
// that the Campaigns cell links straight to `/groups/:id/migrations`, where
// `GroupMigrationsPage` already carries the full member table — so the facts are
// one click away rather than one expander away.
//
// NO STATUS COLUMN (FD-474, owner 2026-09-25). FD-472 put the stacked
// `GroupStatusBar` here — the owner's choice over the board's single pill, on the
// grounds that a group holds N campaigns in N states and one pill has to pick
// one. Seen against real data it earned its place on neither count: an unlabelled
// four-segment bar needs a hover to say anything, and the one question it answers
// is the one the STATE TABS above the table already answer in words, with counts,
// as a filter an operator can act on.
//
// The dimension is not lost, which is why removing it is a trim rather than a
// regression: `deriveGroupState` still drives those tabs, `GroupStatusBar` still
// renders on the group overview page where one group's distribution IS the
// subject, and the CSV export still carries the per-status breakdown.
//
// GROUPS ARE EXECUTION-INERT (FD-ADR-030 §3), and the row menu does not change
// that. FD-ADR-030 §5 and FD-ADR-031 §6 refused a group action menu — but they
// refused one whose items (pause, re-run preflight, delete migration) had NO
// BACKEND OPERATION AT ALL, or mapped onto `abort-wave`, which is irreversible
// and terminal-stamps every device. Every item here is a route that already
// exists. Rename and delete are deliberately NOT duplicated into a dialog:
// `GroupSettingsPage` owns them, including the `409 GROUP_NOT_EMPTY` refusal
// FD-ADR-030 §5 requires, and a second implementation of a delete is exactly the
// kind of drift that decision exists to prevent.

export interface GroupsTableProps {
  groups: MigrationGroupWithRollup[];
  /** True when a filter or search is narrowing the set — changes the empty copy. */
  isFiltered?: boolean;
  isLoading?: boolean;
  onOpenGroup: (groupId: string) => void;
  onOpenCampaigns: (groupId: string) => void;
  onOpenSettings: (groupId: string) => void;
  onClearFilters?: () => void;
}

function columns(
  onOpenGroup: (id: string) => void,
  onOpenCampaigns: (id: string) => void,
  onOpenSettings: (id: string) => void,
): DataTableColumn<MigrationGroupWithRollup>[] {
  return [
    {
      key: 'name',
      header: 'Group name',
      render: (g) => (
        <span className="inline-flex min-w-0 items-center gap-2">
          <FolderOpen size={16} strokeWidth={1.5} style={{ color: TEXT.secondary }} />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenGroup(g.migrationGroupId);
            }}
            className={`${PLAIN_BUTTON} cursor-pointer truncate text-left font-medium hover:underline`}
            style={{ color: TEXT.primary }}
          >
            {g.name}
          </button>
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      width: '26%',
      render: (g) =>
        g.description?.trim() ? (
          <span className="block truncate" style={{ color: TEXT.secondary }}>
            {g.description}
          </span>
        ) : (
          // A group without a description is ordinary, not missing data.
          <span style={{ color: TEXT.disabled }}>—</span>
        ),
    },
    {
      key: 'campaigns',
      header: 'Campaigns',
      width: '130px',
      render: (g) => {
        const n = g.rollup.migrationCount ?? 0;
        if (n === 0) {
          // Not a link — there is nothing behind it. A link that opens an empty
          // tab teaches an operator that links are a guess.
          return <span style={{ color: TEXT.secondary }}>None yet</span>;
        }
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenCampaigns(g.migrationGroupId);
            }}
            className={`${PLAIN_BUTTON} cursor-pointer text-left hover:underline`}
            style={{ color: TEXT.primary }}
          >
            <span style={NUMERIC}>{n.toLocaleString()}</span>
            {n === 1 ? ' campaign' : ' campaigns'}
          </button>
        );
      },
    },
    {
      // "Devices assigned", never "Devices". `DeviceCount` moves only on
      // add/remove-devices and never on wave completion, so it is an ASSIGNMENT
      // count and the header has to say which.
      key: 'deviceCount',
      header: 'Devices assigned',
      width: '150px',
      numeric: true,
      align: 'right',
      render: (g) => (g.rollup.deviceCount ?? 0).toLocaleString(),
    },
    {
      key: 'lastActivity',
      header: 'Last activity',
      width: '130px',
      numeric: true,
      render: (g) => <RelativeTime iso={groupLastActivity(g)} emptyPlaceholder="never" />,
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      width: '56px',
      align: 'center',
      render: (g) => (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <button
              type="button"
              aria-label={`Actions for ${g.name}`}
              onClick={(e) => e.stopPropagation()}
              className={`${PLAIN_BUTTON} cursor-pointer rounded-sm p-1 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1`}
              style={{ color: TEXT.secondary }}
            >
              <MoreHorizontal size={16} strokeWidth={1.5} />
            </button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem onClick={() => onOpenGroup(g.migrationGroupId)}>Open group</MenuItem>
              <MenuItem
                disabled={(g.rollup.migrationCount ?? 0) === 0}
                onClick={() => onOpenCampaigns(g.migrationGroupId)}
              >
                View campaigns
              </MenuItem>
              {/* Rename and delete live here, on the page that already owns them
                  and already surfaces the 409 GROUP_NOT_EMPTY refusal. */}
              <MenuItem onClick={() => onOpenSettings(g.migrationGroupId)}>
                Rename or delete…
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      ),
    },
  ];
}

export default function GroupsTable({
  groups,
  isFiltered,
  isLoading,
  onOpenGroup,
  onOpenCampaigns,
  onOpenSettings,
}: GroupsTableProps) {
  const cols = useMemo(
    () => columns(onOpenGroup, onOpenCampaigns, onOpenSettings),
    [onOpenGroup, onOpenCampaigns, onOpenSettings],
  );

  return (
    <div
      className="overflow-x-auto rounded-lg"
      style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}
    >
      <DataTable
        columns={cols}
        data={groups}
        keyForRow={(g) => g.migrationGroupId}
        onRowClick={(g) => onOpenGroup(g.migrationGroupId)}
        isLoading={isLoading}
        loadingRowCount={5}
        emptyState={
          isFiltered ? (
            <span style={{ color: TEXT.secondary }}>No groups match these filters.</span>
          ) : (
            // Groups are optional (FD-ADR-030), so an empty list is an ordinary
            // state and must not read as a backlog.
            <span style={{ color: TEXT.secondary }}>No groups yet.</span>
          )
        }
      />
    </div>
  );
}

/** Exported for the loading state; the table itself renders nothing while empty. */
export function GroupsTableSkeleton() {
  return (
    <div
      className="flex items-center justify-center rounded-lg px-4 py-12"
      style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}
    >
      <Spinner size="small" label="Loading groups" />
    </div>
  );
}
